// Library Methods
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import { Button, Grid, TextField } from "@mui/material";

// Components
import ExamCardList from "../../../../containers/Patients/ExamsList/ExamCardList";
import DefaultDrawer from "../../../../components/UI/DefaultDrawer";

// Styles
import {
  CustomTextFieldOutlinedInputProps,
  CustomTextFieldOutlined,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../../styles/muiStylesHelper";

// Utilities
import { useExamListContext } from "../../../../contexts/ExamListContext";

const AddExamPackageSlider = ({
  isLoading,
  visible,
  onClose,
  onConfirm,
  packageName,
  setPackageName,
}) => {
  // internationalization
  const { t } = useTranslation();

  const { examCards } = useExamListContext();

  const selectedExamCards = useMemo(
    () => examCards.selected,
    [examCards.selected]
  );

  const shouldDisable = useMemo(() => {
    const examHasInvalidValues = selectedExamCards.some((examCard) => {
      const { examType, eyeCheckboxes } = examCard;
      return !(examType && (eyeCheckboxes?.left || eyeCheckboxes?.right));
    });

    return Boolean(
      isLoading ||
        !selectedExamCards.length ||
        examHasInvalidValues ||
        !packageName?.length
    );
  }, [isLoading, selectedExamCards, packageName]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleEscKey);
    } else {
      document.removeEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [visible, onClose]);

  return (
    <DefaultDrawer
      isOpen={visible}
      onClose={onClose}
      showCloseButton={false}
      title={t("add_exam_package")}
      customStyles={{
        width: { xs: "100%", md: "80%" },
        height: "100%",
      }}
    >
      <Grid
        position="sticky"
        top="72px"
        zIndex={100}
        backgroundColor="#FFF"
        display="flex"
        justifyContent="space-between"
        columnGap="20px"
        padding="20px"
        borderBottom="1px solid #435B6D7A"
      >
        <Grid
          display="flex"
          columnGap="20px"
          flexDirection={{ xs: "column", sm: "row" }}
          float={{ xs: "end", sm: "right" }}
          width={{ xs: "100%", sm: "450px" }}
        >
          <TextField
            id="outlined-basic"
            required
            value={packageName}
            onChange={(e) => {
              setPackageName(e.target.value);
            }}
            label={t("exam_package_name")}
            variant="outlined"
            fullWidth
            sx={() => CustomTextFieldOutlined({ mt: 0, mr: 0 })}
            InputProps={{
              sx: CustomTextFieldOutlinedInputProps,
            }}
            inputProps={{ maxLength: 32 }}
          />
        </Grid>

        <Grid
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "flex-start",
            gap: "20px",
          }}
        >
          <Button
            variant="text"
            disabled={isLoading}
            sx={ModalCustomCancel}
            onClick={onClose}
          >
            {t("button_cancel")}
          </Button>
          <Button
            variant="outlined"
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
            onClick={onConfirm}
          >
            {isLoading ? t("word_loading") : t("button_add")}
          </Button>
        </Grid>
      </Grid>

      <ExamCardList />
    </DefaultDrawer>
  );
};

export default AddExamPackageSlider;
